import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Clock, Share2, Copy, Check, Loader2, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /insights/:slug — public article detail page.
 *
 * Above the fold: dark orbital hero with breadcrumb + eyebrow + title + meta
 * strip. Below: two-column layout on desktop — long-form markdown body on the
 * left, sticky "On this page" TOC + share + related on the right.
 */
export default function InsightDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, article: null, related: [] });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, article: null, related: [] });
      try {
        const res = await api.get(`/insights/${slug}`);
        if (!cancelled) setState({ loading: false, article: res.data.article, related: res.data.related });
      } catch {
        if (!cancelled) setState({ loading: false, article: null, related: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Fire a view ping — once per browser session per slug (sessionStorage guard
  // prevents rapid-refresh double counting while still capturing real reads).
  // Includes utm_source (from URL) + document.referrer so admin analytics can
  // bucket views by traffic channel.
  useEffect(() => {
    if (!slug) return;
    const key = `connexis_insight_viewed_${slug}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* private mode — proceed anyway */ }

    let utm_source = null;
    try {
      utm_source = new URLSearchParams(window.location.search).get("utm_source");
    } catch { /* noop */ }
    const referrer = (typeof document !== "undefined" && document.referrer) || null;

    api.post(`/insights/${slug}/view`, { utm_source, referrer })
      .catch(() => { /* silent — analytics is best-effort */ });
  }, [slug]);

  const { article } = state;

  useSeo({
    title: article ? `${article.seo_title || article.title} · Connexis Insights` : "Loading…",
    description: article?.seo_description || article?.excerpt || "",
    canonical: article ? `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/insights/${article.slug}` : undefined,
    type: "article",
  });

  // Extract H2 headings from markdown for the TOC
  const toc = React.useMemo(() => {
    if (!article?.body_md) return [];
    const lines = article.body_md.split("\n");
    const out = [];
    for (const line of lines) {
      const m = line.match(/^##\s+(.+?)\s*$/);
      if (m) {
        const text = m[1].trim();
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        out.push({ id, text });
      }
    }
    return out;
  }, [article]);

  const share = async () => {
    const url = window.location.href;
    // Fire the share tracking ping — best-effort, always
    api.post(`/insights/${slug}/share`).catch(() => { /* silent */ });
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.excerpt, url });
        return;
      } catch { /* user cancelled — fall through */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  if (state.loading) {
    return (
      <MarketingLayout>
        <div className="min-h-[60vh] grid place-items-center text-slate-400">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </MarketingLayout>
    );
  }

  if (!article) {
    return (
      <MarketingLayout>
        <div className="min-h-[60vh] grid place-items-center text-center px-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Article not found.</h2>
            <p className="mt-2 text-slate-500">This piece may have moved or is still in draft.</p>
            <button
              onClick={() => nav("/insights")}
              className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-slate-400 px-4 h-10 text-[13px] font-medium text-slate-700"
              data-testid="insights-404-back"
            >
              <ArrowLeft className="size-3.5" /> All insights
            </button>
          </div>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <article className="bg-white" data-testid="insight-detail-page">
        {/* ─── HERO ─────────────────────────────────────────── */}
        <header className="relative overflow-hidden bg-[#070c1a] text-white">
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="absolute -top-40 left-1/3 w-[620px] h-[620px] rounded-full bg-[#0055FF]/[0.18] blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-32 -right-20 w-[520px] h-[520px] rounded-full bg-emerald-500/[0.08] blur-[120px] pointer-events-none" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-16 pb-14 lg:pt-20 lg:pb-16">
            <Link
              to="/insights"
              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase text-white/50 hover:text-white font-semibold"
              data-testid="insight-back"
            >
              <ArrowLeft className="size-3" /> Insights
            </Link>

            <div className="mt-6 flex items-center gap-1.5 flex-wrap">
              {article.sector_tags?.slice(0, 3).map((t) => (
                <Link
                  key={t}
                  to={`/insights?tag=${encodeURIComponent(t)}`}
                  className="inline-flex items-center rounded-full bg-white/[0.06] border border-white/[0.12] px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-white/70 hover:border-white/25 hover:text-white transition-colors"
                >
                  {t}
                </Link>
              ))}
            </div>

            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[56px] tracking-[-0.03em] font-semibold text-white leading-[1.05]" data-testid="insight-title">
              {article.title}
            </h1>
            <p className="mt-5 text-[17px] text-white/60 leading-relaxed max-w-2xl">
              {article.excerpt}
            </p>

            <div className="mt-8 flex items-center gap-4 text-[13px] text-white/50 flex-wrap">
              <span className="font-medium text-white/80">{article.author}</span>
              <span className="text-white/25">·</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="size-3" /> {article.reading_time_mins} min read</span>
              <span className="text-white/25">·</span>
              <span>{formatDate(article.published_at)}</span>
              {article.views >= 100 && (
                <>
                  <span className="text-white/25">·</span>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/[0.12] px-2.5 py-0.5 text-[11.5px] font-medium tabular-nums text-white/75"
                    data-testid="insight-views-badge"
                    title={`${article.views.toLocaleString()} views`}
                  >
                    <Eye className="size-3 text-emerald-300" />
                    {article.views.toLocaleString()} views
                  </span>
                </>
              )}
            </div>
          </div>
        </header>

        {/* ─── BODY ─────────────────────────────────────────── */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-[1fr_260px] gap-10 lg:gap-14">
            {/* Main markdown body */}
            <div className="min-w-0">
              <div className="prose-connexis" data-testid="insight-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}
                >
                  {article.body_md}
                </ReactMarkdown>
              </div>

              {/* Bottom CTA back to /pay-per-call */}
              <div className="mt-14 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Ready to route calls?</div>
                    <h3 className="mt-1.5 text-xl sm:text-2xl tracking-[-0.02em] font-semibold text-slate-900">
                      Post a campaign or place your supply on Connexis.
                    </h3>
                  </div>
                  <Link
                    to="/pay-per-call"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white px-4 h-11 text-[13px] font-semibold shadow-[0_8px_24px_rgba(0,85,255,0.35)] whitespace-nowrap shrink-0"
                    data-testid="insight-ppc-cta"
                  >
                    Talk to the desk <ArrowRight className="size-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Sticky sidebar */}
            <aside className="lg:sticky lg:top-24 self-start space-y-6">
              {toc.length > 0 && (
                <nav className="rounded-xl border border-slate-200 bg-white p-5" data-testid="insight-toc">
                  <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-slate-500">On this page</div>
                  <ul className="mt-3 space-y-1.5 text-[13px]">
                    {toc.map((h) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className="block py-0.5 text-slate-600 hover:text-[#0055FF] transition-colors leading-snug"
                        >
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <button
                type="button"
                onClick={share}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-white px-4 h-10 text-[12.5px] font-semibold text-slate-700"
                data-testid="insight-share"
              >
                {copied ? <><Check className="size-3.5 text-emerald-500" /> Link copied</> : <><Share2 className="size-3.5" /> Share article</>}
              </button>

              {state.related.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="insight-related">
                  <div className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-slate-500 mb-3 inline-flex items-center gap-1.5">
                    <Sparkles className="size-3 text-[#0055FF]" /> Related
                  </div>
                  <ul className="space-y-4">
                    {state.related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to={`/insights/${r.slug}`}
                          className="group block"
                          data-testid={`insight-related-${r.slug}`}
                        >
                          <div className="text-[13.5px] font-semibold text-slate-900 leading-snug group-hover:text-[#0044CC] transition-colors">
                            {r.title}
                          </div>
                          <div className="mt-1 text-[11.5px] text-slate-500 inline-flex items-center gap-1">
                            <Clock className="size-3" /> {r.reading_time_mins} min · {formatDate(r.published_at, true)}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </article>
    </MarketingLayout>
  );
}

/* ─── Markdown component overrides ───────────────────────────── */
const mdComponents = {
  h2: ({ node, children, ...props }) => {
    const text = React.Children.toArray(children).join("");
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return (
      <h2 id={id} className="mt-12 mb-4 text-[28px] tracking-[-0.025em] font-semibold text-slate-900 scroll-mt-24" {...props}>
        {children}
      </h2>
    );
  },
  h3: ({ children, ...props }) => (
    <h3 className="mt-8 mb-3 text-[20px] tracking-[-0.02em] font-semibold text-slate-900" {...props}>{children}</h3>
  ),
  p: ({ children, ...props }) => (
    <p className="my-4 text-[16px] text-slate-700 leading-[1.75]" {...props}>{children}</p>
  ),
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      className="text-[#0055FF] hover:underline underline-offset-2 decoration-[#0055FF]/40"
      {...props}
    >
      {children}
    </a>
  ),
  ul: ({ children, ...props }) => (
    <ul className="my-5 pl-5 space-y-2 list-disc marker:text-slate-400" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="my-5 pl-5 space-y-2 list-decimal marker:text-slate-500 marker:font-mono marker:text-[13px]" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li className="text-[16px] text-slate-700 leading-[1.7]" {...props}>{children}</li>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-slate-900" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic text-slate-700" {...props}>{children}</em>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote className="my-6 pl-5 border-l-2 border-[#0055FF]/40 text-slate-600 italic" {...props}>{children}</blockquote>
  ),
  code: ({ inline, children, ...props }) => inline
    ? <code className="rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[13px] font-mono text-slate-800" {...props}>{children}</code>
    : <code className="block rounded-lg bg-slate-900 text-slate-100 p-4 text-[13px] font-mono leading-relaxed overflow-x-auto" {...props}>{children}</code>,
  hr: () => <hr className="my-10 border-t border-slate-200" />,
  table: ({ children }) => (
    <div className="my-6 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-[13.5px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => (
    <th className="text-left px-4 py-2.5 font-semibold text-slate-700 text-[11.5px] uppercase tracking-[0.14em] border-b border-slate-200">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-slate-700 border-b border-slate-100 last:border-b-0">{children}</td>
  ),
};

function formatDate(iso, short = false) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (short) return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}
